import { CommonModule, isPlatformBrowser } from '@angular/common';
import type { OnInit } from '@angular/core';
import { Component, HostListener, Inject, Input, PLATFORM_ID } from '@angular/core';
import { LoadingIndicatorComponent } from '@core-mkt/shared/components/loading-indicator/loading-indicator.component';
import { ProductCardComponent } from '@core-mkt/shared/components/product-card/product-card/product-card.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCaretDown, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { cloneDeep } from 'lodash-es';

import type { PageDataResponse } from '@core-mkt/resolvers/page-data/page-data-response';
import { EnvService } from '@core-mkt/services/env/env.service';
import { GeolocationService } from '@core-mkt/services/geolocation/geolocation.service';
import type { ProductSection } from '@core-mkt/shared/components/product-card/product-card/product-card';
import type { PriceSelectorItem } from './price-selector';
import { PriceSelector } from './price-selector';

@Component({
  selector: 'price-filter',
  templateUrl: './price-selector.component.html',
  styleUrls: ['./price-selector.component.scss'],
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, LoadingIndicatorComponent, ProductCardComponent],
})
export class PriceSelectorComponent implements OnInit {
  @Input() data: PriceSelector;
  resolverData: PageDataResponse;
  faCaretDown = faCaretDown;
  faChevronDown = faChevronDown;
  dropdownCollapse = false;
  selectedItem: PriceSelectorItem;
  selectorInstruction = 'Filter information by state';
  selectorCardTitle = '<h2>AARP Smart Driver Course Pricing</h2>';
  selectorCardCopy =
    '<p class="small font-extrabold">${this.finalPrice} for AARP members</p>\n<p class="small font-extrabold">${this.crossedOutPrice} for non-members</p>\n<p class="eyebrow">Money-Back Guarantee!</p>';
  selectorList: PriceSelectorItem[];
  selectorProductCard: ProductSection = {
    styles: ['bg-white text-vis-regular'],
    productCardList: [
      {
        cardBackgroundColor: 'bg-white text-vis-regular',
        cardTextColor: 'text-vis-regular',
        cardTitle: this.selectorCardTitle,
        ctaLabel: 'Register Now',
        ctaLink: null,
        productCtaTag: 'DRIVER-SAFETY-BTN-CLK-PRICING-GET_STARTED',
        ctaNofollow: false,
        ctaNewTab: false,
        learnMoreLabel: null,
        learnMoreLink: null,
        learnMoreNewTab: false,
        learnMoreNofollow: false,
        productEntry: [
          {
            productPageUrl: null,
            productState: 'AL',
            xgritBundleCoupon: null,
            xgritCoupon: null,
            xgritProductDisableRsa: false,
            xgritProductId: 'W81twjzQXDW6Fw8r',
          },
        ],
        showSavings: true,
        showPriceText: true,
        starCaption: null,
        starRating: '',
        textContent: this.selectorCardCopy,
        topContent: '',
        topBackgroundColor: '',
        topTextColor: '',
        topContentStyles: [],
      },
    ],
    backgroundColor: 'bg-white text-vis-regular',
    backgroundImage: [],
    hideCta: false,
    ctaStyle: 'primary',
    sectionTitle: null,
    textColor: '',
    typeHandle: 'aarpProductCards',
    useCompactCards: false,
  };
  showProductCard = false;
  showCaret = false;
  // Loading state for geolocation
  public isLoadingState: boolean = false;
  // Selected state for geolocation
  public selectedState = '';
  private isBrowser: boolean;

  get dropdownPlaceholder(): string {
    return this.selectedItem?.state || 'Alabama';
  }

  get cardTitle(): string {
    if (this.selectorCardTitle !== null && this.selectorCardTitle.includes('${state}')) {
      return this.selectorCardTitle.replace('${state}', this.selectedItem?.state);
    }
    return `<h2>${this.selectedItem?.state} AARP Smart Driver Course Pricing</h2>`;
  }

  constructor(
    public geolocation: GeolocationService,
    private envService: EnvService,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    // Only subscribe to loading state in browser
    if (this.isBrowser) {
      this.geolocation?.isLoading.subscribe((isLoading) => {
        this.isLoadingState = isLoading;
      });
    }
  }

  ngOnInit(): void {
    // Initialize data
    this.selectorList = cloneDeep(this.data.selectorList);
    this.selectedItem = this.selectorList[0];
    this.selectorInstruction = this.data.selectorInstructions;
    this.selectorCardCopy = this.data.cardText;
    this.selectorCardTitle = this.data.cardTitle;
    this.showCaret = this.envService.get.brandConfig.showCaretInPriceSelector ?? false;

    // Set default state and show product card immediately (for SSR)
    this.selectedState = this.selectedItem?.state || '';
    this.updateProductCard();
    this.showProductCard = true;

    // Only attempt geolocation in browser
    if (this.isBrowser) {
      this.geolocation.loadLocationCookie();

      this.geolocation.stateAbbr.subscribe((stateAbbr) => {
        if (stateAbbr) {
          this.selectedState = this.geolocation.getStateName(stateAbbr);
          const foundItem = this.selectorList.find((item) => item.state === this.selectedState);

          if (foundItem) {
            this.selectedItem = foundItem;
            this.updateProductCard();
          }
        }
      });
    }
  }

  updateProduct(filterItem: PriceSelectorItem): void {
    this.dropdownCollapse = false;
    this.selectedItem = filterItem;
    this.updateProductCard();
    // Update the cookie value if user selects a different state
    this.geolocation.updateLocationCookie(this.geolocation.getStateAbbr(filterItem.state));
  }

  updateProductMobile(index: string): void {
    const filterItem = this.selectorList[parseInt(index)];
    this.dropdownCollapse = false;
    this.selectedItem = filterItem;
    this.updateProductCard();
    // Update the cookie value if user selects a different state
    this.geolocation.updateLocationCookie(this.geolocation.getStateAbbr(filterItem.state));
  }

  updateProductCard(): void {
    const updatedProductCard = cloneDeep(this.selectorProductCard);
    if (this.selectedItem && this.selectedItem.product) {
      updatedProductCard.productCardList[0].productEntry = this.selectedItem.product;

      updatedProductCard.productCardList[0].productEntry[0].disclaimer = this.selectedItem.disclaimer;
      if (this.envService.get.xgritRedirectUrl) {
        updatedProductCard.productCardList[0].ctaLink = this.envService.get.xgritRedirectUrl;
      }
      updatedProductCard.productCardList[0].textContent = this.selectorCardCopy;
      updatedProductCard.productCardList[0].cardTitle = this.cardTitle;
      this.selectorProductCard = updatedProductCard;
    }
  }

  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.dropdownCollapse = !this.dropdownCollapse;
  }

  @HostListener('document:click')
  closeDropdown(): void {
    if (this.dropdownCollapse) {
      this.dropdownCollapse = false;
    }
  }
}
